import { RiAddLine, RiDeleteBin6Line, RiLoader4Line, RiStore2Line } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { $api } from "@/api/query";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

/**
 * System -> Stores. OrgAdmin only.
 *
 * `beforeLoad` bounces non-admins to /dashboard. That is the second of the
 * three enforcement layers (nav hides, route bounces, backend 403s); the
 * backend `OrgAdmin` extractor is the authoritative one.
 */
export const Route = createFileRoute("/_app/stores")({
	beforeLoad: async () => {
		const res = await fetch("/auth/me", { credentials: "include" });
		if (res.ok) {
			const me = await res.json();
			const role = me?.role ?? me?.user?.role;
			if (role !== "owner" && role !== "admin") {
				throw redirect({ to: "/dashboard" });
			}
		}
	},
	component: StoresPage,
});

interface Store {
	id: string;
	name: string;
	isActive: boolean;
	notificationEmail: string | null;
	branchCount: number;
	userCount: number;
	createdAt: string;
}

interface AllocatableBranch {
	number: number;
	name: string | null;
	isActive: boolean;
	storeId: string | null;
	storeName: string | null;
}

interface AssignableUser {
	userId: string;
	email: string;
	name: string | null;
	role: string;
	storeId: string | null;
	storeName: string | null;
}

/**
 * A store's shared role mailbox (`occasioni@burresi.com`), edited in place.
 *
 * This is where a store's order offers are DELIVERED, so the column shows every
 * store's address at a glance and makes an unset one obvious - a blank address
 * falls back to the store's assigned users, which is fine when staff exist and
 * silently reaches nobody when they do not.
 *
 * Saves on Enter or blur; Escape reverts. Only PATCHes when the value actually
 * changed, so tabbing through the table does not fire pointless writes.
 */
/// Active/Inactive toggle.
///
/// This used to be a read-only Badge with no way to change it, and the flag
/// gated nothing server-side - an admin deactivating a store would reasonably
/// believe orders stop being offered to it, and they did not. The backend now
/// filters offer eligibility on `store.is_active`; this makes the control
/// reachable. Staff of an inactive store deliberately KEEP access, so orders
/// they already accepted can still be completed.
function StoreActiveCell({ store, onSaved }: { store: Store; onSaved: () => void }) {
	const [saving, setSaving] = useState(false);

	async function toggle(next: boolean) {
		setSaving(true);
		const res = await fetch(`/api/v1/stores/${store.id}`, {
			method: "PATCH",
			credentials: "include",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ isActive: next }),
		});
		setSaving(false);
		if (res.ok) {
			toast.success(
				next
					? `${store.name} will receive new orders again`
					: `${store.name} will not be offered new orders`,
			);
			onSaved();
		} else {
			toast.error(`Could not update ${store.name}`);
		}
	}

	return (
		<div className="flex items-center gap-2">
			<Switch
				checked={store.isActive}
				disabled={saving}
				onCheckedChange={toggle}
				aria-label={`${store.name} active`}
			/>
			<span className="text-muted-foreground text-xs">
				{store.isActive ? "Active" : "Inactive"}
			</span>
		</div>
	);
}

function StoreEmailCell({ store, onSaved }: { store: Store; onSaved: () => void }) {
	const [editing, setEditing] = useState(false);
	const [value, setValue] = useState(store.notificationEmail ?? "");
	const [saving, setSaving] = useState(false);

	async function save() {
		const next = value.trim();
		if (next === (store.notificationEmail ?? "")) {
			setEditing(false);
			return;
		}
		setSaving(true);
		const res = await fetch(`/api/v1/stores/${store.id}`, {
			method: "PATCH",
			credentials: "include",
			headers: { "content-type": "application/json" },
			// "" is meaningful here: it CLEARS the address server-side. That is
			// why the field is sent as a raw string and never coerced to null.
			body: JSON.stringify({ notificationEmail: next }),
		});
		setSaving(false);
		if (res.ok) {
			setEditing(false);
			toast.success(
				next
					? `Notifications for ${store.name} go to ${next}`
					: `Address cleared for ${store.name}`,
			);
			onSaved();
		} else {
			// The backend returns a readable 400 for a malformed address.
			const msg = await res.text();
			toast.error(msg.replace(/^"|"$/g, "") || "Could not save the address");
		}
	}

	if (!editing) {
		return (
			<button
				type="button"
				className="w-full truncate text-left text-sm hover:underline"
				onClick={() => {
					setValue(store.notificationEmail ?? "");
					setEditing(true);
				}}
				aria-label={`Edit notification email for ${store.name}`}
			>
				{store.notificationEmail ?? <span className="text-muted-foreground">(not set)</span>}
			</button>
		);
	}

	return (
		<Input
			autoFocus
			type="email"
			value={value}
			disabled={saving}
			aria-label={`Notification email for ${store.name}`}
			onChange={(e) => setValue(e.target.value)}
			onBlur={save}
			onKeyDown={(e) => {
				if (e.key === "Enter") save();
				if (e.key === "Escape") {
					setValue(store.notificationEmail ?? "");
					setEditing(false);
				}
			}}
			className="h-8"
		/>
	);
}

/**
 * The org-wide address copied on EVERY store order, whichever store it went to
 * ("Alle Bestellung auch bitte immer an farfetch@burresi.com").
 *
 * It sits here rather than under Integrations because an operator thinks about
 * it alongside the per-store addresses, not alongside SFTP credentials.
 */
function OrderBccCard() {
	const query = $api.useQuery("get", "/api/v1/stores/order-bcc" as never);
	const current = (query.data as { email: string | null } | undefined)?.email ?? null;
	const [value, setValue] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	// Null local state = "not edited yet", so the server value shows through
	// until the operator types. Seeding from the query would need an effect and
	// would fight the refetch.
	const shown = value ?? current ?? "";

	async function save() {
		setSaving(true);
		const res = await fetch("/api/v1/stores/order-bcc", {
			method: "PUT",
			credentials: "include",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ email: shown.trim() }),
		});
		setSaving(false);
		if (res.ok) {
			toast.success(
				shown.trim() ? `Every store order is copied to ${shown.trim()}` : "Copy address cleared",
			);
			setValue(null);
			query.refetch();
		} else {
			const msg = await res.text();
			toast.error(msg.replace(/^"|"$/g, "") || "Could not save the address");
		}
	}

	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle className="text-base">Order notifications</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2">
				<Label htmlFor="order-bcc">Copy every store order to</Label>
				<div className="flex flex-col gap-2 sm:flex-row">
					<Input
						id="order-bcc"
						// NOT type="email": that input type validates a SINGLE address and
						// marks a comma-separated list invalid, which would block saving
						// more than one copy recipient. The backend splits on commas and
						// skips anything unparseable, so a bad entry costs that one
						// address rather than the whole mail.
						type="text"
						placeholder="online@burresi.com, info@cavort.de"
						title="One or more addresses, separated by commas."
						value={shown}
						disabled={saving || query.isLoading}
						onChange={(e) => setValue(e.target.value)}
						className="sm:max-w-sm"
					/>
					<Button onClick={save} disabled={saving || query.isLoading}>
						{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" aria-hidden />}
						Save
					</Button>
				</div>
				<p className="text-sm text-muted-foreground">
					Sent in addition to each store's own address. Separate several addresses with commas.
					Leave empty to send only to the stores.
				</p>
			</CardContent>
		</Card>
	);
}

function StoresPage() {
	const queryClient = useQueryClient();
	const [createOpen, setCreateOpen] = useState(false);
	const [newName, setNewName] = useState("");
	const [newEmail, setNewEmail] = useState("");
	const [expanded, setExpanded] = useState<string | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<Store | null>(null);

	// `as never` is this repo's pattern for endpoints newer than the last
	// generated schema.d.ts. It also erases the result type, so each query is
	// narrowed to the shape actually used (data + the loading/error/refetch
	// surface) rather than sprinkling casts at every use site.
	type Q<T> = {
		data: T | undefined;
		isLoading: boolean;
		error: unknown;
		refetch: () => void;
	};
	const stores = $api.useQuery("get", "/api/v1/stores" as never) as Q<Store[]>;
	const branches = $api.useQuery("get", "/api/v1/stores/branches" as never) as Q<
		AllocatableBranch[]
	>;
	const users = $api.useQuery("get", "/api/v1/stores/users" as never) as Q<AssignableUser[]>;

	const storeList = stores.data ?? [];
	const branchList = branches.data ?? [];
	const userList = users.data ?? [];

	function invalidateAll() {
		queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/stores"] });
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/stores/branches"],
		});
		queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/stores/users"] });
	}

	async function createStore() {
		const name = newName.trim();
		if (!name) return;
		const res = await fetch("/api/v1/stores", {
			method: "POST",
			credentials: "include",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ name, notificationEmail: newEmail.trim() || null }),
		});
		if (res.ok) {
			toast.success(`Store "${name}" created`);
			setNewName("");
			setNewEmail("");
			setCreateOpen(false);
			invalidateAll();
		} else {
			// The backend returns a readable 400 for a duplicate name; surface it
			// rather than a generic failure.
			const body = await res.json().catch(() => null);
			toast.error(body?.error ?? "Could not create the store");
		}
	}

	async function deleteStore(store: Store) {
		const res = await fetch(`/api/v1/stores/${store.id}`, {
			method: "DELETE",
			credentials: "include",
		});
		if (res.ok) {
			toast.success(`Store "${store.name}" deleted; its branches were released`);
			setDeleteTarget(null);
			invalidateAll();
		} else {
			toast.error("Could not delete the store");
		}
	}

	if (stores.isLoading) {
		return (
			<div className="space-y-4 p-6">
				<Skeleton className="h-8 w-48" />
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	if (stores.error) {
		return (
			<div className="p-6">
				<Card>
					<CardContent className="flex flex-col items-start gap-3 pt-6">
						<p role="alert" className="text-sm text-destructive">
							Could not load stores.
						</p>
						<Button variant="outline" onClick={() => stores.refetch()}>
							Retry
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="space-y-6 p-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="flex items-center gap-2 text-2xl font-semibold">
						<RiStore2Line className="size-6" aria-hidden />
						Stores
					</h1>
					<p className="text-sm text-muted-foreground">
						Group branches into stores. Orders reach a store through its branches, and its staff see
						only that store.
					</p>
				</div>
				<Button onClick={() => setCreateOpen(true)}>
					<RiAddLine className="mr-1 size-4" aria-hidden />
					New store
				</Button>
			</div>

			<OrderBccCard />

			{storeList.length === 0 ? (
				<Card>
					<CardContent className="pt-6 text-sm text-muted-foreground">
						No stores yet. Create one, then allocate its branches.
					</CardContent>
				</Card>
			) : (
				<Card>
					<CardContent className="p-0">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Notification email</TableHead>
									<TableHead className="w-28">Branches</TableHead>
									<TableHead className="w-24">Users</TableHead>
									<TableHead className="w-24">Active</TableHead>
									<TableHead className="w-40 text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{storeList.map((store) => (
									<>
										<TableRow key={store.id}>
											<TableCell className="font-medium">{store.name}</TableCell>
											<TableCell className="max-w-56">
												<StoreEmailCell store={store} onSaved={invalidateAll} />
											</TableCell>
											<TableCell>{store.branchCount}</TableCell>
											<TableCell>{store.userCount}</TableCell>
											<TableCell>
												<StoreActiveCell store={store} onSaved={invalidateAll} />
											</TableCell>
											<TableCell className="space-x-2 text-right">
												<Button
													variant="outline"
													size="sm"
													onClick={() => setExpanded(expanded === store.id ? null : store.id)}
												>
													{expanded === store.id ? "Close" : "Allocate"}
												</Button>
												<Button
													variant="ghost"
													size="sm"
													aria-label={`Delete ${store.name}`}
													onClick={() => setDeleteTarget(store)}
												>
													<RiDeleteBin6Line className="size-4" aria-hidden />
												</Button>
											</TableCell>
										</TableRow>
										{expanded === store.id && (
											<TableRow key={`${store.id}-detail`}>
												<TableCell colSpan={6} className="bg-muted/30">
													<div className="grid gap-6 md:grid-cols-2">
														<BranchAllocation
															storeId={store.id}
															branches={branchList}
															onSaved={invalidateAll}
														/>
														<UserAllocation
															storeId={store.id}
															users={userList}
															onSaved={invalidateAll}
														/>
													</div>
												</TableCell>
											</TableRow>
										)}
									</>
								))}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			)}

			<Dialog open={createOpen} onOpenChange={setCreateOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>New store</DialogTitle>
						<DialogDescription>
							Give the store a name and where its orders should be mailed. Allocate its branches
							next.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="store-name">Name</Label>
							<Input
								id="store-name"
								value={newName}
								onChange={(e) => setNewName(e.target.value)}
								placeholder="Laden 74"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="store-email">Notification email (optional)</Label>
							<Input
								id="store-email"
								type="email"
								value={newEmail}
								onChange={(e) => setNewEmail(e.target.value)}
								placeholder="loft@burresi.com"
							/>
							<p className="text-sm text-muted-foreground">
								A shared mailbox for the store. Leave empty to mail its assigned staff instead.
							</p>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setCreateOpen(false)}>
							Cancel
						</Button>
						<Button onClick={createStore} disabled={!newName.trim()}>
							Create
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={deleteTarget !== null}
				onOpenChange={(open) => !open && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete this store?</AlertDialogTitle>
						<AlertDialogDescription>
							{deleteTarget?.name} will be removed. Its branches are released for another store to
							claim, and its staff lose access to the store app. Orders already assigned are not
							deleted.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={() => deleteTarget && deleteStore(deleteTarget)}>
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

/**
 * Branch allocation. A branch owned by ANOTHER store renders disabled with the
 * owner's name, so the exclusivity rule is explained in the UI instead of only
 * surfacing as a save error. The DB primary key is still the backstop for a
 * concurrent edit, and that collision renders as a readable message.
 *
 * Active branches sort first: the Prohandel tenant is shared with a sister
 * boutique (500 Budapester, 700 Cettire are not ours), so the list must not
 * imply every branch is allocatable.
 */
function BranchAllocation({
	storeId,
	branches,
	onSaved,
}: {
	storeId: string;
	branches: AllocatableBranch[];
	onSaved: () => void;
}) {
	const mine = branches.filter((b) => b.storeId === storeId).map((b) => b.number);
	// Re-seed local selection whenever the server's allocation changes (e.g.
	// after another admin saved). Deriving that from a key beats an effect here:
	// it needs no dependency array, so there is no lint to suppress, and the
	// reset is visibly tied to "the server value changed".
	const serverKey = mine.join(",");
	const [syncedKey, setSyncedKey] = useState(serverKey);
	const [selected, setSelected] = useState<number[]>(mine);
	const [saving, setSaving] = useState(false);

	if (syncedKey !== serverKey) {
		setSyncedKey(serverKey);
		setSelected(mine);
	}

	async function save() {
		setSaving(true);
		const res = await fetch(`/api/v1/stores/${storeId}/branches`, {
			method: "PUT",
			credentials: "include",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ branchNumbers: selected }),
		});
		setSaving(false);
		if (res.ok) {
			toast.success("Branches updated");
			onSaved();
		} else {
			const body = await res.json().catch(() => null);
			toast.error(body?.error ?? "Could not update branches");
		}
	}

	const active = branches.filter((b) => b.isActive);
	const inactive = branches.filter((b) => !b.isActive);

	function row(b: AllocatableBranch) {
		const ownedByOther = b.storeId !== null && b.storeId !== storeId;
		return (
			<div key={b.number} className="flex items-center gap-2">
				<Checkbox
					id={`branch-${storeId}-${b.number}`}
					checked={selected.includes(b.number)}
					disabled={ownedByOther}
					onCheckedChange={(checked) =>
						setSelected((prev) =>
							checked ? [...prev, b.number] : prev.filter((n) => n !== b.number),
						)
					}
				/>
				<Label
					htmlFor={`branch-${storeId}-${b.number}`}
					className={ownedByOther ? "text-muted-foreground" : undefined}
				>
					{b.number} - {b.name ?? "(unnamed)"}
				</Label>
				{ownedByOther && (
					<Badge variant="outline" className="text-xs">
						{b.storeName}
					</Badge>
				)}
			</div>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Branches</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				{active.length > 0 && (
					<div className="space-y-2">
						<p className="text-xs font-medium uppercase text-muted-foreground">Active</p>
						{active.map(row)}
					</div>
				)}
				{inactive.length > 0 && (
					<div className="space-y-2">
						<p className="text-xs font-medium uppercase text-muted-foreground">Inactive</p>
						{inactive.map(row)}
					</div>
				)}
				{branches.length === 0 && (
					<p className="text-sm text-muted-foreground">
						No branches known yet. Run a Prohandel sync first.
					</p>
				)}
				<Button size="sm" onClick={save} disabled={saving}>
					{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" aria-hidden />}
					Save branches
				</Button>
			</CardContent>
		</Card>
	);
}

/** User allocation. One store per user, so a member already elsewhere shows it. */
function UserAllocation({
	storeId,
	users,
	onSaved,
}: {
	storeId: string;
	users: AssignableUser[];
	onSaved: () => void;
}) {
	const mine = users.filter((u) => u.storeId === storeId).map((u) => u.userId);
	// Same derived-key re-seed as the branch picker above.
	const serverKey = mine.join(",");
	const [syncedKey, setSyncedKey] = useState(serverKey);
	const [selected, setSelected] = useState<string[]>(mine);
	const [saving, setSaving] = useState(false);

	if (syncedKey !== serverKey) {
		setSyncedKey(serverKey);
		setSelected(mine);
	}

	async function save() {
		setSaving(true);
		const res = await fetch(`/api/v1/stores/${storeId}/users`, {
			method: "PUT",
			credentials: "include",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ userIds: selected }),
		});
		setSaving(false);
		if (res.ok) {
			toast.success("Store staff updated");
			onSaved();
		} else {
			const body = await res.json().catch(() => null);
			toast.error(body?.error ?? "Could not update store staff");
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Staff</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				{users.map((u) => {
					const ownedByOther = u.storeId !== null && u.storeId !== storeId;
					return (
						<div key={u.userId} className="flex items-center gap-2">
							<Checkbox
								id={`user-${storeId}-${u.userId}`}
								checked={selected.includes(u.userId)}
								disabled={ownedByOther}
								onCheckedChange={(checked) =>
									setSelected((prev) =>
										checked ? [...prev, u.userId] : prev.filter((id) => id !== u.userId),
									)
								}
							/>
							<Label
								htmlFor={`user-${storeId}-${u.userId}`}
								className={ownedByOther ? "text-muted-foreground" : undefined}
							>
								{u.email}
							</Label>
							<Badge variant="outline" className="text-xs">
								{u.role}
							</Badge>
							{ownedByOther && (
								<Badge variant="outline" className="text-xs">
									{u.storeName}
								</Badge>
							)}
						</div>
					);
				})}
				{users.length === 0 && (
					<p className="text-sm text-muted-foreground">No org members found.</p>
				)}
				<Button size="sm" onClick={save} disabled={saving}>
					{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" aria-hidden />}
					Save staff
				</Button>
			</CardContent>
		</Card>
	);
}
